import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { useAuth } from "@/lib/auth";
import Logo from "@/components/Logo";

const STAGES = [
  { to: "/discover", label: "Discover", num: "01" },
  { to: "/plan", label: "Plan", num: "02" },
  { to: "/apply", label: "Apply", num: "03" },
  { to: "/prepare", label: "Prepare", num: "04" },
  { to: "/thrive", label: "Thrive", num: "05" },
];

export default function Nav() {
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  const overlayHero = isHome && !scrolled;

  return (
    <>
      <motion.header
        initial={{ y: -30, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: [0.2, 0.7, 0.2, 1] }}
        className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-500 ${
          overlayHero ? "bg-transparent" : "bg-[#f5f1e8]/85 backdrop-blur-xl border-b border-[#0d1b3e]/10"
        }`}
        data-testid="site-nav"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 h-16 md:h-20 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="nav-logo-link">
            <Logo mode={overlayHero ? "light" : "dark"} />
          </Link>

          <nav className="hidden lg:flex items-center gap-1" data-testid="nav-stages">
            {STAGES.map((s) => (
              <NavLink
                key={s.to}
                to={s.to}
                data-testid={`nav-link-${s.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `group px-3 py-2 text-[13px] tracking-[0.14em] uppercase font-medium transition-colors ${
                    overlayHero ? "text-white/80 hover:text-white" : "text-[#0d1b3e]/70 hover:text-[#0d1b3e]"
                  } ${isActive ? (overlayHero ? "!text-white" : "!text-[#0d1b3e]") : ""}`
                }
              >
                <span className="text-[10px] mr-1.5 text-[#b8892b] font-semibold">{s.num}</span>
                {s.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-2">
            <Link
              to="/pricing"
              data-testid="nav-pricing-link"
              className={`hidden md:inline-flex px-3 py-2 text-[12px] tracking-[0.14em] uppercase font-medium ${
                overlayHero ? "text-white/80 hover:text-white" : "text-[#0d1b3e]/70 hover:text-[#0d1b3e]"
              }`}
            >
              Pricing
            </Link>
            {user ? (
              <>
                <Link to="/dashboard" data-testid="nav-dashboard-link"
                  className={`hidden md:inline-flex px-3 py-2 text-[12px] tracking-[0.14em] uppercase font-medium ${overlayHero ? "text-white" : "text-[#0d1b3e]"}`}>
                  Dashboard
                </Link>
                <button onClick={logout} data-testid="nav-logout-btn"
                  className={`hidden md:inline-flex btn-sharp ${overlayHero ? "!bg-white !text-[#0d1b3e] !border-white" : ""}`}>
                  Sign out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" data-testid="nav-login-link"
                  className={`hidden md:inline-flex px-3 py-2 text-[12px] tracking-[0.14em] uppercase font-medium ${overlayHero ? "text-white/80 hover:text-white" : "text-[#0d1b3e]/70 hover:text-[#0d1b3e]"}`}>
                  Log in
                </Link>
                <Link to="/advisor" data-testid="nav-cta-btn"
                  className={`!hidden md:!inline-flex btn-sharp ${overlayHero ? "!bg-[#b8892b] !border-[#b8892b] hover:!bg-white hover:!text-[#0d1b3e]" : "btn-sharp-gold"}`}>
                  Start Your Free Plan
                </Link>
              </>
            )}
            <button onClick={() => setOpen(true)} data-testid="nav-menu-open"
              className={`lg:hidden ml-1 p-2 ${overlayHero ? "text-white" : "text-[#0d1b3e]"}`}>
              <Menu size={22} />
            </button>
          </div>
        </div>
      </motion.header>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] bg-[#0d1b3e] text-white flex flex-col"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-6 h-16">
              <Logo mode="light" />
              <button onClick={() => setOpen(false)} data-testid="nav-menu-close" className="p-2">
                <X size={22} />
              </button>
            </div>
            <div className="flex-1 flex flex-col justify-center px-8 gap-6">
              {STAGES.map((s, i) => (
                <motion.div key={s.to}
                  initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
                  transition={{ delay: 0.05 * i, duration: 0.5 }}>
                  <Link to={s.to} className="flex items-baseline gap-4 border-b border-white/10 pb-4"
                    data-testid={`mobile-nav-${s.label.toLowerCase()}`}>
                    <span className="text-[#b8892b] text-sm font-semibold">{s.num}</span>
                    <span className="serif text-4xl">{s.label}</span>
                  </Link>
                </motion.div>
              ))}
              <div className="mt-6 flex flex-col gap-3">
                <Link to="/advisor" className="btn-sharp btn-sharp-gold !text-white justify-center">Start Your Free Plan</Link>
                <Link to="/pricing" className="btn-sharp btn-sharp-outline !text-white !border-white/60 justify-center">Pricing</Link>
                {user ? (
                  <Link to="/dashboard" className="btn-sharp btn-sharp-outline !text-white !border-white/60 justify-center">Dashboard</Link>
                ) : (
                  <Link to="/login" className="btn-sharp btn-sharp-outline !text-white !border-white/60 justify-center">Log in</Link>
                )}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
